import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Col,
  Collapse,
  Form,
  FormFeedback,
  FormGroup,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import Select from "react-select"
import FormLabel from "components/Extra/FormLabel"
import { FormInput } from "components/Extra/FormInput"
import classNames from "classnames"
import { useTranslation } from "react-i18next"
import { createProductCategory, updateProductCategory } from "../../../../store/product-categories/actions"
import { Category } from "../Categories"

interface AddNewCategoryProps {
  isOpen: boolean
  toggle: () => void
  category: Category | null
}

export const AddNewCategory: React.FC<AddNewCategoryProps> = ({
  isOpen,
  toggle,
  category,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const isEditMode = category !== null

  // Get categories from Redux for parent category dropdown
  const { categories } = useSelector((state: any) => state.ProductCategories)

  const [openFirstAccordion, setOpenFirstAccordion] = useState("1")
  const [openSecondAccordion, setOpenSecondAccordion] = useState("1")

  const [selectedParentCategory, setSelectedParentCategory] = useState<{
    id: number
    name: string
  } | null>(null)

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      enName: category?.eN_NAME || "",
      arName: category?.aR_NAME || "",
      categoryDescription: "",
      uiOrderId: category?.uI_ORDER_ID || 0,
      isActive: category?.iS_ACTIVE ?? true,
    },
    validationSchema: Yup.object({
      enName: Yup.string().required(t("categories_categoryNameValidationMessage2") as string),
      arName: Yup.string().required(t("categories_categoryNameValidationMessage2") as string),
      categoryDescription: Yup.string().trim(),
      uiOrderId: Yup.number().required(t("categories_categoryNameValidationMessage2") as string),
      isActive: Yup.boolean(),
    }),
    onSubmit: values => {
      const categoryData = {
        eN_NAME: values.enName,
        aR_NAME: values.arName,
        uI_ORDER_ID: values.uiOrderId,
        parenT_ID: selectedParentCategory?.id ?? null,
        iS_ACTIVE: values.isActive,
      }

      if (isEditMode && category) {
        dispatch(updateProductCategory(category.id, categoryData))
      } else {
        dispatch(createProductCategory(categoryData))
      }

      validation.resetForm()
      setSelectedParentCategory(null)
      toggle()
    },
  })

  useEffect(() => {
    if (isOpen) {
      validation.resetForm()
      // Set parent category if editing
      if (category?.parenT_ID) {
        const parent = categories.find((cat: any) => cat.id === category.parenT_ID)
        if (parent) {
          setSelectedParentCategory({
            id: parent.id,
            name: parent.eN_NAME,
          })
        }
      } else {
        setSelectedParentCategory(null)
      }
    }
  }, [isOpen, category])

  // Filter categories for parent dropdown (exclude current category when editing)
  const availableParentCategories = categories
    .filter((cat: any) => isEditMode ? cat.id !== category?.id : true)
    .map((cat: any) => ({
      id: cat.id,
      name: cat.eN_NAME,
    }))

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <p className="text-capitalize fw-semibold font-size-24">
          {isEditMode ? t("categories_editCategory") : t("categories_addNewCategory")}
        </p>
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <div className="accordion shadow-md rounded-3" id="accordion">
            <div className="accordion-item">
              <h2 className="accordion-header" id="headingOne">
                <button
                  className={classNames("accordion-button", "fw-medium", {
                    collapsed: !openFirstAccordion,
                  })}
                  type="button"
                  onClick={() =>
                    setOpenFirstAccordion(
                      openFirstAccordion === "0" ? "1" : "0"
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  {t("common_basicInfo")}
                </button>
              </h2>
              <Collapse
                isOpen={openFirstAccordion === "1"}
                className="accordion-collapse"
              >
                <div className="accordion-body">
                  <FormInput
                    label={t("categories_englishName") as string}
                    name="enName"
                    type="text"
                    placeholder={t("categories_enterCategoryNameEnglish") as string}
                    validation={validation}
                  />
                  <FormInput
                    label={t("categories_arabicName") as string}
                    name="arName"
                    type="text"
                    placeholder={t("categories_enterCategoryNameArabic") as string}
                    validation={validation}
                  />
                  <FormGroup>
                    <FormLabel htmlFor="parentCategoryId">
                      {t("categories_parentCategory")}
                    </FormLabel>
                    <Select
                      id="parentCategoryId"
                      name="parentCategoryId"
                      value={selectedParentCategory}
                      onChange={(option: any) => {
                        setSelectedParentCategory(option)
                      }}
                      options={availableParentCategories}
                      getOptionLabel={(option: any) => option.name}
                      getOptionValue={(option: any) => option.id}
                      placeholder={t("categories_selectParentCategoryOptional") as string}
                      classNamePrefix="select2-selection"
                      isClearable
                      isSearchable
                    />
                  </FormGroup>
                  <FormInput
                    label={t("categories_categoryDescription") as string}
                    name="categoryDescription"
                    type="textarea"
                    placeholder={
                      t("categories_enterCategoryDescription") as string
                    }
                    validation={validation}
                    rows="3"
                    disabled
                  />
                  <FormInput
                    label={t("categories_displayOrder") as string}
                    name="uiOrderId"
                    type="number"
                    placeholder={t("categories_enterDisplayOrder") as string}
                    validation={validation}
                  />
                </div>
              </Collapse>
            </div>
          </div>
          <div className="accordion shadow-md rounded-3 mt-4" id="accordion">
            <div className="accordion-item">
              <h2 className="accordion-header" id="headingOne">
                <button
                  className={classNames("accordion-button", "fw-medium", {
                    collapsed: !openSecondAccordion,
                  })}
                  type="button"
                  onClick={() =>
                    setOpenSecondAccordion(
                      openSecondAccordion === "0" ? "1" : "0"
                    )
                  }
                >
                  {t("common_visibility")}
                </button>
              </h2>
              <Collapse
                isOpen={openSecondAccordion === "1"}
                className="accordion-collapse"
              >
                <div className="accordion-body">
                  <FormGroup className="d-flex align-items-center justify-content-between gap-3">
                    <FormLabel htmlFor="isActive">
                      <span className="fw-bold">
                        {t("categories_changeVisibility")}
                      </span>{" "}
                      ({t("categories_categoryVisibility")})
                    </FormLabel>
                    <div className="form-check form-switch mb-2">
                      <input
                        name="isActive"
                        type="checkbox"
                        className="form-check-input"
                        id="isActive"
                        checked={validation.values.isActive}
                        onClick={e => {
                          e.stopPropagation()
                          validation.setFieldValue(
                            "isActive",
                            !validation.values.isActive
                          )
                        }}
                        onChange={e => {
                          e.stopPropagation()
                        }}
                        onBlur={validation.handleBlur}
                      />
                    </div>
                  </FormGroup>
                </div>
              </Collapse>
            </div>
          </div>
          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="submit"
                  className={`btn btn-primary save-customer`}
                  disabled={!(validation.isValid && validation.dirty)}
                >
                  {isEditMode ? t("common_save") : t("categories_createCategory")}
                </button>
                <button
                  onClick={toggle}
                  type="button"
                  className="btn btn-cancel text-primary border border-primary"
                >
                  {t("common_cancel")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}
